import { DialogComponent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';
import '@theme/product-form';

class V4CardQuickDialog extends DialogComponent {
  #abortController = new AbortController();
  #imageRequestId = 0;
  #busy = false;

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    super.connectedCallback();
    this.addEventListener('change', this.#handleVariantChange, { signal: this.#abortController.signal });
    this.addEventListener('submit', this.#guardSubmit, { capture: true, signal: this.#abortController.signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  showDialog() {
    super.showDialog();
    requestAnimationFrame(() => {
      this.querySelector('[data-v4-variant-option]:checked')?.nextElementSibling?.scrollIntoView({ block: 'nearest' });
    });
  }

  #guardSubmit = (event) => {
    const option = this.querySelector('[data-v4-variant-option]:checked');
    if (this.#busy || !option || option.dataset.available !== 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  #handleVariantChange = (event) => {
    if (this.#busy || !(event.target instanceof HTMLInputElement) || !event.target.matches('[data-v4-variant-option]')) return;

    const option = event.target;
    const price = this.querySelector('[data-v4-quick-price]');
    const selectedLabels = this.querySelectorAll('[data-v4-quick-selected], [data-v4-quick-footer-selected]');
    const image = this.querySelector('[data-v4-quick-image]');
    const variantId = this.querySelector('[ref="variantId"]');
    const button = this.querySelector('[ref="addToCartButton"]');

    if (price && option?.dataset.price) price.textContent = option.dataset.price;
    if (variantId instanceof HTMLInputElement) {
      variantId.value = option.value;
      variantId.disabled = false;
      variantId.dispatchEvent(new Event('change', { bubbles: true }));
    }
    selectedLabels.forEach((label) => {
      label.textContent = option.dataset.title || '';
    });

    if (image instanceof HTMLImageElement && option.dataset.image) this.#updatePreviewImage(image, option);

    if (button instanceof HTMLButtonElement) {
      button.disabled = option.dataset.available !== 'true';
      const label = button.querySelector('.add-to-cart-text__content');
      if (label) label.textContent = option.dataset.available === 'true' ? this.dataset.addLabel : this.dataset.soldOutLabel;
    }
    const animation = this.querySelector('add-to-cart-component');
    if (animation && option.dataset.image) animation.dataset.productVariantMedia = option.dataset.image;
    const error = this.querySelector('[data-v4-quick-error]');
    if (error) error.textContent = '';
  };

  #updatePreviewImage(image, option) {
    const src = option.dataset.image;
    const requestId = ++this.#imageRequestId;
    if (!src) return;
    image.removeAttribute('srcset');
    image.alt = option.dataset.title || '';
    if (image.getAttribute('src') === src) return;

    const preloader = new Image();

    const applyImage = () => {
      if (requestId !== this.#imageRequestId || !option.checked) return;
      image.src = src;
      image.alt = option.dataset.title || '';
    };

    preloader.addEventListener('load', applyImage, { once: true });
    preloader.addEventListener('error', applyImage, { once: true });
    preloader.src = src;
    if (preloader.complete) applyImage();
  }

  #handleCartUpdate = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    this.#busy = true;
    const button = this.querySelector('[ref="addToCartButton"]');
    const label = button?.querySelector('.add-to-cart-text__content');
    const error = this.querySelector('[data-v4-quick-error]');
    if (error) error.textContent = '';
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    if (label) label.textContent = this.dataset.addingLabel;
    this.querySelectorAll('[data-v4-variant-option]').forEach((option) => { option.disabled = true; });

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) { if (error) error.textContent = this.dataset.errorLabel; return; }
        this.closeDialog();
      })
      .catch(() => {
        if (error) error.textContent = this.dataset.errorLabel;
      })
      .finally(() => {
        this.#busy = false;
        const option = this.querySelector('[data-v4-variant-option]:checked');
        if (button) { button.disabled = option?.dataset.available !== 'true'; button.removeAttribute('aria-busy'); }
        if (label) label.textContent = option?.dataset.available === 'true' ? this.dataset.addLabel : this.dataset.soldOutLabel;
        this.querySelectorAll('[data-v4-variant-option]').forEach((input) => { input.disabled = false; });
      });
  };
}

if (!customElements.get('v4-card-quick-dialog')) {
  customElements.define('v4-card-quick-dialog', V4CardQuickDialog);
}

class V4HomeCardAction extends HTMLElement {
  #abortController;
  #busy = false;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    this.addEventListener('submit', (event) => {
      if (this.#busy) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, { capture: true, signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
  }

  disconnectedCallback() { this.#abortController?.abort(); }

  #handleCartUpdate = (event) => {
    const button = this.querySelector('quick-add-component [ref="addToCartButton"]');
    if (!button || !event.promise) return;
    const error = this.querySelector('[data-home-cart-error]');
    this.#busy = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (error) error.textContent = '';
    event.promise.then(({ detail }) => {
      if (detail?.didError && error) error.textContent = this.dataset.errorLabel;
    }).catch(() => {
      if (error) error.textContent = this.dataset.errorLabel;
    }).finally(() => {
      this.#busy = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    });
  };
}

if (!customElements.get('v4-home-card-action')) {
  customElements.define('v4-home-card-action', V4HomeCardAction);
}
